import React from 'react';
import moment from 'moment';
import Form from 'react-bootstrap/Form';
import Stack from 'react-bootstrap/Stack';
import { formatMedicare, formatNumberString, formatPhone } from '../utils';
import {
  getBmiDisplayFromEvents,
  getLatestEventByLabels,
  NOT_CALCULATED_LABEL,
} from '../utils/bmi';
import { getAjbwDisplayFromEvents } from '../utils/ajbw';
import { HpSelect } from './HpSelect';
import PatientProfileHeaderAssignedTo from './Header/PatientProfileHeaderAssignedTo';
import { generateTemporaryPatient } from '../utils/utils';
import PatientEpisodeDetails from './Header/PatientEpisodeDetails';
import { SectionField, ValueType } from './AdditionalSection/types';
import { ClinicTO, PatientTO, TeamTO } from '../../../../lib';
import { usePatientProfile } from '../hooks';
import PatientMyPathTriageCategory from './Header/PatientMypathTriageCategory';
import { EpisodeDetailOptionsField } from './PatientField/EpisodeDetailOptionsField';

interface Value {
  clinics: ClinicTO[];
  [key: string]: any;
}

interface ProfileFieldProps {
  field: SectionField;
  value: Value;
  editProfile: boolean;
  onInputChange: (
    e: React.ChangeEvent<any>,
    clinicIndex?: number,
    episodeIndex?: number,
  ) => void;
  dataKey: string;
  onToggleClinics: () => void;
  openClinics: boolean;
  patientProfileStatus: any;
  clinic: ClinicTO;
  healthProfessionals: any[];
  setOpenClinics: (open: boolean) => void;
}

export const ProfileField: React.FC<ProfileFieldProps> = ({
  field,
  value,
  editProfile,
  onInputChange,
  dataKey,
  onToggleClinics,
  openClinics,
  patientProfileStatus,
  clinic,
  healthProfessionals,
  setOpenClinics,
}) => {
  const {
    form: { register },
  } = usePatientProfile();

  const currentClinic = value.clinics[0];
  const latestEpisodeIndex =
    currentClinic.episodes && currentClinic.episodes.length > 0
      ? currentClinic.episodes.length - 1
      : 0;
  const latestEpisode =
    currentClinic.episodes && currentClinic.episodes.length > 0
      ? currentClinic.episodes[latestEpisodeIndex]
      : null;

  const handleLatestEpisodeChange = (e: any) => {
    onInputChange(e, 0, latestEpisodeIndex);
  };

  const mainComponent = React.useMemo(() => {
    switch (field.formatType) {
      case 'date':
        return (
          <Stack direction="horizontal" gap={2} className="labelRow">
            <Form.Label className="labelBold">{field.title}</Form.Label>
            {(!editProfile || (editProfile && !field.editable)) && (
              <Form.Label className="labelSimple">
                {moment(new Date(value[dataKey])).format('DD/MM/YYYY')}
              </Form.Label>
            )}
            {editProfile && field.editable && (
              <Form.Control
                type="date"
                name={dataKey}
                defaultValue={
                  moment(new Date(value[dataKey])).format('YYYY-MM-DD') || '-'
                }
                onChange={handleLatestEpisodeChange}
                className="form-control-xs"
              />
            )}
          </Stack>
        );
      case 'clinic':
        return (
          <Stack direction="horizontal" gap={2} className="labelRow">
            <Form.Label className="labelBold">{field.title}</Form.Label>

            {value.clinics.length === 1 && (
              <Form.Label className="labelSimple">
                {currentClinic.clinic || '-'}
              </Form.Label>
            )}

            {value.clinics.length > 1 && (
              <Form.Label className="labelSimple">
                Multiple
                <a
                  className="ms-2 simpleLink"
                  onClick={onToggleClinics}
                  aria-controls="multiple-clinics"
                  aria-expanded={openClinics}
                  data-target="#"
                  multiple-clinics
                >
                  {`${openClinics ? 'Hide' : 'See'} Details`}
                </a>
              </Form.Label>
            )}
          </Stack>
        );

      case 'teams':
        const teams: TeamTO[] = value.teams;
        return (
          <Stack
            direction="horizontal"
            gap={2}
            className="labelRow align-items-start"
          >
            <Form.Label className="labelBold">{field.title}</Form.Label>
            {teams && teams.length === 0 ? (
              <Form.Label className="labelSimple">-</Form.Label>
            ) : (
              <div className="d-flex flex-column">
                {teams &&
                  teams.map((team, index) => (
                    <Form.Label
                      key={`${team.id}-${index}`}
                      className="labelSimple"
                    >
                      {team.clinicName}
                    </Form.Label>
                  ))}
              </div>
            )}
          </Stack>
        );

      case 'primaryTeams':
        return (
          <Stack direction="horizontal" gap={2} className="labelRow">
            <Form.Label className="labelBold">{field.title}</Form.Label>

            {value.clinics.length === 1 && (
              <Form.Label className="labelSimple">
                {latestEpisode?.primary_teams || '-'}
              </Form.Label>
            )}

            {value.clinics.length > 1 && (
              <Form.Label className="labelSimple">
                Multiple
                <a
                  className="ms-2 simpleLink"
                  onClick={onToggleClinics}
                  aria-controls="multiple-clinics"
                  aria-expanded={openClinics}
                  data-target="#"
                  multiple-clinics
                >
                  {`${openClinics ? 'Hide' : 'See'} Details`}
                </a>
              </Form.Label>
            )}
          </Stack>
        );

      case 'episode':
        return (
          <Stack direction="horizontal" gap={2} className="labelRow">
            <Form.Label className="labelBold">Episode</Form.Label>

            {(!editProfile || (editProfile && !field.editable)) && (
              <Form.Label className="labelSimple">
                {latestEpisode?.episode_closed ? 'Closed' : 'Open'}
              </Form.Label>
            )}

            {editProfile && field.editable && (
              <Form.Select
                className="form-select-xs"
                onChange={handleLatestEpisodeChange}
                name="episodeClosed"
                id="episodeClosed"
                defaultValue={String(
                  (latestEpisode as any)?.[dataKey] ?? 'false',
                )}
              >
                <option key="open" value={'false'}>
                  Open
                </option>
                <option key="close" value={'true'}>
                  Closed
                </option>
              </Form.Select>
            )}
          </Stack>
        );
      case 'healthProf':
        return (
          <>
            {(() => {
              if (!value.clinics || value.clinics.length === 0) {
                return '-';
              }

              const healthProfessionalsFromClinic =
                (currentClinic?.healthProfessionals as string[]) ?? [];

              // TODO: Bad practice here for just using the type assertion
              // to avoid the typescript error, we probably need to modify toPatientProfileTO() under Patient.ts
              // to correctly map the clinics to ClinicTO[]
              let healthProfessionals: string[] = [];
              if (!Array.isArray(healthProfessionalsFromClinic)) {
                healthProfessionals = (
                  (
                    healthProfessionalsFromClinic as unknown as Record<
                      number,
                      string[]
                    >
                  )?.[currentClinic.id] || []
                ).filter(Boolean);
              } else {
                healthProfessionals =
                  healthProfessionalsFromClinic.filter(Boolean);
              }

              const overwriteClinic = {
                ...currentClinic,
                episodes: [latestEpisode],
              };
              return (
                <HpSelect
                  label="Health prof."
                  editProfile={editProfile}
                  patientProfileStatus={patientProfileStatus}
                  clinic={overwriteClinic}
                  onInputChange={handleLatestEpisodeChange}
                  healthProfessionals={healthProfessionals}
                />
              );
            })()}
          </>
        );
      case 'gender':
        return (
          <Stack direction="horizontal" gap={2} className="labelRow">
            <Form.Label className="labelBold">{field.title}</Form.Label>
            {editProfile && field.editable ? (
              <Form.Group controlId="gender">
                <Form.Select
                  className="form-select-xs mb-1"
                  onChange={handleLatestEpisodeChange}
                  name="gender"
                  id="gender"
                >
                  <option key="F" value="F" selected={value[dataKey] === 'F'}>
                    Female
                  </option>
                  <option key="I" value="I" selected={value[dataKey] === 'I'}>
                    Intersex or indeterminate
                  </option>
                  <option key="M" value="M" selected={value[dataKey] === 'M'}>
                    Male
                  </option>
                  <option key="S" value="S" selected={value[dataKey] === 'S'}>
                    Not stated/inadequately described
                  </option>
                </Form.Select>
              </Form.Group>
            ) : (
              <Form.Label className="labelSimple">
                {value[dataKey] === 'M'
                  ? 'Male'
                  : value[dataKey] === 'F'
                    ? 'Female'
                    : value[dataKey] === 'I'
                      ? 'Intersex or indeterminate'
                      : value[dataKey] === 'S'
                        ? 'Not stated /inadequately described'
                        : '-'}
              </Form.Label>
            )}
          </Stack>
        );
      case 'contactPhone':
      case 'phone':
        return (
          <Stack direction="horizontal" gap={2} className="labelRow">
            <Form.Label className="labelBold">{field.title}</Form.Label>
            {(!editProfile || (editProfile && !field.editable)) && (
              <Form.Label className="labelSimple">
                {value[dataKey] ? formatPhone(value[dataKey]) : '-'}
              </Form.Label>
            )}
            {editProfile && field.editable && (
              <Form.Control
                {...register(dataKey)}
                className="form-control-xs"
                onChange={handleLatestEpisodeChange}
                name={dataKey}
                id={dataKey}
                type="text"
                placeholder={field.title}
                defaultValue={value[dataKey] || ''}
              />
            )}
          </Stack>
        );
      case 'contactEmail':
      case 'email':
        return (
          <Stack direction="horizontal" gap={2} className="labelRow">
            <Form.Label className="labelBold">{field.title}</Form.Label>
            {(!editProfile || (editProfile && !field.editable)) && (
              <Form.Label className="labelSimple">
                {value[dataKey] || '-'}
              </Form.Label>
            )}
            {editProfile && field.editable && (
              <Form.Control
                {...register(dataKey)}
                className="form-control-xs"
                onChange={handleLatestEpisodeChange}
                name={dataKey}
                id={dataKey}
                type="email"
                placeholder="Email"
                defaultValue={value[dataKey] || '-'}
              />
            )}
          </Stack>
        );
      case 'IHI':
        return (
          <Stack
            direction="horizontal"
            gap={2}
            className="labelRow"
            data-tip={
              value.ihi != null
                ? 'Status: ' +
                  (value.ihi_status ? value.ihi_status : '-') +
                  ', Record: ' +
                  (value.ihi_record ? value.ihi_record : '-')
                : ''
            }
          >
            <label className="labelBold">IHI</label>
            <label className="labelSimple">
              {value.ihi != null && value.ihi != ''
                ? formatNumberString(value.ihi)
                : '-'}
            </label>
          </Stack>
        );
      case 'relationship':
        return (
          <Stack direction="horizontal" gap={2} className="labelRow">
            <Form.Label className="labelBold">{field.title}</Form.Label>
            {(!editProfile || (editProfile && !field.editable)) && (
              <Form.Label className="labelSimple">
                {value[dataKey] || '-'}
              </Form.Label>
            )}
            {editProfile && field.editable && (
              <Form.Control
                className="form-control-xs"
                onChange={handleLatestEpisodeChange}
                name={dataKey}
                id={dataKey}
                type="text"
                placeholder="Relationship"
                defaultValue={value[dataKey] || ''}
              />
            )}
          </Stack>
        );
      case 'metaData':
        return (
          <Stack direction="horizontal" gap={2} className="labelRow">
            <label className="labelBold">{field.title}</label>
            {(() => {
              // Check if clinics array exists and has at least one clinic
              if (!value.clinics || value.clinics.length === 0) {
                return <label className="labelSimple">-</label>;
              }

              // Get the most recent episode
              const latestEpisode =
                value.clinics[0].episodes &&
                value.clinics[0].episodes.length > 0
                  ? value.clinics[0].episodes[
                      value.clinics[0].episodes.length - 1
                    ]
                  : null;

              // Check if the episode has details
              if (!latestEpisode || !latestEpisode.details) {
                return <label className="labelSimple">-</label>;
              }

              const fieldValue =
                latestEpisode.details[field.field ?? field.title];

              if (!fieldValue) {
                return <label className="labelSimple">-</label>;
              }

              if (Array.isArray(fieldValue)) {
                return fieldValue.map((item, index) => (
                  <React.Fragment key={index}>
                    <label className="labelSimple">{item}</label>
                    <br />
                  </React.Fragment>
                ));
              }

              return <label className="labelSimple">{fieldValue}</label>;
            })()}
          </Stack>
        );

      case 'medicare':
        return (
          <Stack direction="horizontal" gap={2} className="labelRow">
            <Form.Label className="labelBold">{field.title}</Form.Label>
            {(!editProfile || (editProfile && !field.editable)) && (
              <Form.Label className="labelSimple">
                {formatMedicare(value[dataKey]) || '-'}
              </Form.Label>
            )}

            {editProfile && field.editable && (
              <Form.Control
                className="form-control-xs"
                onChange={handleLatestEpisodeChange}
                name={dataKey}
                id={dataKey}
                type="text"
                placeholder={field.title}
                defaultValue={value[dataKey] || '-'}
              />
            )}
          </Stack>
        );

      case 'UR/CBIS':
        return (
          <Stack direction="horizontal" gap={2} className="labelRow">
            <Form.Label className="labelBold">{field.title}</Form.Label>
            {(!editProfile || (editProfile && !field.editable)) && (
              <Form.Label className="labelSimple">
                {value.clinics[0].client_no || '-'}
              </Form.Label>
            )}

            {editProfile && field.editable && (
              <Form.Control
                className="form-control-xs"
                onChange={handleLatestEpisodeChange}
                name={dataKey}
                id={dataKey}
                type="text"
                placeholder={field.title}
                defaultValue={value[dataKey] || '-'}
              />
            )}
          </Stack>
        );
      case 'detail':
        return (
          <Stack direction="horizontal" gap={2} className="labelRow">
            <Form.Label className="labelBold">{field.title}</Form.Label>

            {value.clinics.length === 1 && (
              <Form.Label className="labelSimple">
                {value.clinics[0].clinic_details?.toString() || '-'}
              </Form.Label>
            )}
            {value.clinics.length > 1 && (
              <Form.Label className="labelSimple">
                Multiple
                <a
                  className="ms-2 simpleLink"
                  onClick={() => setOpenClinics(!openClinics)}
                  aria-controls="multiple-clinics"
                  aria-expanded={openClinics}
                  data-target="#"
                  multiple-clinics
                >
                  {`${openClinics ? 'Hide' : 'See'} Details`}
                </a>
              </Form.Label>
            )}
          </Stack>
        );
      case 'episode_detail':
        if (field.key.includes('assigned_to')) {
          const patient = generateTemporaryPatient(value);
          return (
            <PatientProfileHeaderAssignedTo
              favouritedClinicians={value.favouritedClinicians}
              patient={patient as PatientTO}
            />
          );
        }
        if (field.editType === ValueType.OPTIONS) {
          return (
            <EpisodeDetailOptionsField
              title={field.title}
              editable={Boolean(field.editable)}
              clinicId={currentClinic?.id}
              options={field.options || []}
            />
          );
        }
        return (
          <PatientEpisodeDetails
            field={field}
            isEditing={editProfile}
            onInputChange={handleLatestEpisodeChange as any}
          />
        );
      case 'mypath_triage_category':
        return (
          <PatientMyPathTriageCategory
            field={field}
            onInputChange={onInputChange}
          />
        );
      case 'events':
        const allEpisodeEvents = Array.isArray(value.usr_episodes_details)
          ? value.usr_episodes_details
          : [];
        const eventData = getLatestEventByLabels(allEpisodeEvents, [
          field.title,
          field.field,
        ]);

        const formattedValue = eventData
          ? `${eventData.detail?.fhir.valueQuantity.value}${eventData.detail?.fhir.valueQuantity.unit}`
          : '-';

        const formattedDate = eventData
          ? moment(eventData.event_date).format('DD MMM YY')
          : '';

        //not sure
        const datakey = field.title?.toLowerCase().replace(' ', '_');

        return (
          <Stack direction="horizontal" gap={2} className="labelRow">
            <Form.Label className="labelBold">{field.title}</Form.Label>
            {(!editProfile || (editProfile && !field.editable)) && (
              <Form.Label className="labelSimple">
                {formattedValue}{' '}
                <span style={{ color: '#888' }}>
                  {formattedDate && `(${formattedDate})`}
                </span>
              </Form.Label>
            )}
            {editProfile && field.editable && (
              <Stack direction="horizontal" gap={2}>
                <Form.Control
                  className="form-control-xs"
                  onChange={onInputChange}
                  name={`${datakey}.value`}
                  id={`${datakey}-value`}
                  type="text"
                  placeholder={`${field.title} Value`}
                  defaultValue={
                    eventData ? eventData.detail.fhir.valueQuantity.value : ''
                  }
                />
                <Form.Control
                  className="form-control-xs"
                  onChange={(e) => onInputChange(e)}
                  name={`${datakey}.date`}
                  id={`${datakey}-date`}
                  type="date"
                  placeholder={`${field.title} Date`}
                  defaultValue={
                    eventData
                      ? moment(eventData.event_date).format('YYYY-MM-DD')
                      : ''
                  }
                />
              </Stack>
            )}
          </Stack>
        );
      case 'bmi': {
        const allEvents = Array.isArray(value.usr_episodes_details)
          ? value.usr_episodes_details
          : [];
        const { displayValue, dateStr } = getBmiDisplayFromEvents(allEvents);
        return (
          <Stack direction="horizontal" gap={2} className="labelRow">
            <Form.Label className="labelBold">{field.title}</Form.Label>
            <Form.Label className="labelSimple">
              {displayValue}{' '}
              <span style={{ color: '#888' }}>
                {displayValue !== NOT_CALCULATED_LABEL &&
                  dateStr &&
                  `(${dateStr})`}
              </span>
            </Form.Label>
          </Stack>
        );
      }
      case 'ajbw': {
        const allEvents = Array.isArray(value.usr_episodes_details)
          ? value.usr_episodes_details
          : [];

        const { displayValue, dateStr } = getAjbwDisplayFromEvents(
          allEvents,
          value.gender,
        );
        return (
          <Stack direction="horizontal" gap={2} className="labelRow">
            <Form.Label className="labelBold">{field.title}</Form.Label>
            <Form.Label className="labelSimple">
              {displayValue}{' '}
              <span style={{ color: '#888' }}>
                {displayValue !== NOT_CALCULATED_LABEL &&
                  dateStr &&
                  `(${dateStr})`}
              </span>
            </Form.Label>
          </Stack>
        );
      }

      default:
        return (
          <Stack direction="horizontal" gap={2} className="labelRow">
            <Form.Label className="labelBold">{field.title}</Form.Label>
            {(!editProfile || (editProfile && !field.editable)) && (
              <Form.Label className="labelSimple">
                {value[dataKey] || '-'}
              </Form.Label>
            )}

            {editProfile && field.editable && (
              <Form.Control
                className="form-control-xs"
                onChange={handleLatestEpisodeChange}
                name={dataKey}
                id={dataKey}
                type="text"
                placeholder={field.title}
                defaultValue={value[dataKey] || ''}
              />
            )}
          </Stack>
        );
    }
  }, [
    field,
    value,
    editProfile,
    onInputChange,
    dataKey,
    onToggleClinics,
    openClinics,
    patientProfileStatus,
    clinic,
    healthProfessionals,
    setOpenClinics,
  ]);

  return <div className="d-flex gap-2">{mainComponent}</div>;
};
